/* ============================================================
   androidRecipe.js — Resipi post telefon (skeleton KONGSI)
   ============================================================
   "Buku panduan" langkah-langkah post, disimpan SEKALI sebagai DATA
   (bukan Firestore — dihantar bersama hosting macam skrip remote). Tiap
   telefon pelanggan ikut buku yang sama; yang BERBEZA antara telefon =
   "peta" koordinat (androidCalibration.js), bukan buku ini.

   3 LAPIS cari sasaran (dikendali androidRunner.js), ikut turutan:
     1. selector  — baca nama/resource-id butang (skrin diam). KONGSI semua
        pelanggan versi Shopee sama. Percuma, laju. "Stabil berbulan".
     2. coord     — koordinat tersimpan dari Test telefon itu (skrin bergerak).
     3. vision    — AI tengok screenshot + cari butang (self-healing). Rakam
        koordinat baharu → lain kali guna lapis 2.
   Semua lapis gagal → langkah ERROR → runner JATUH ke Draft (jaring).

   ⚠️ Selector di bawah = diukur pada Redmi 14C (29 Okt 2026). Skrin bergerak
   (feed/kamera/editor/Post) TIADA selector → vision/coord. Butiran ukuran:
   memory project_android_auto_publisher_poc.

   kind langkah:
     launch        — buka app (pkg)
     tap           — tekan satu butang (selector → coord → vision)
     pick_video    — pilih video SESAAT dalam galeri (vision/coord)
     reveal_tap    — skrin preview: ketuk video dulu (tunjuk controls) → tap sasaran
     input_caption — tap medan caption → taip {{caption}}
     tag_product   — sub-flow tag produk dari {{product_link}}
     toggle_ai     — hidupkan "AI-generated label"
     finish        — Post (mod 'post') ATAU Drafts (mod 'draft')
   ============================================================ */

import { TIKTOK_PKG, tiktokSteps, runTiktokPhone } from './androidTiktok';
import { FB_PKG, facebookSteps, runFacebookPhone } from './androidFacebook';

// Satu langkah = satu baris Stepper di UI. labelMs/labelEn untuk paparan.
// selector null → terus ke coord/vision. calibratable → runner rakam
// koordinat selepas vision berjaya (jadi "peta" telefon pelanggan).

export const SHOPEE_RECIPE = {
  appId: 'shopee',
  pkg: 'com.shopee.my',
  // Naikkan bila langkah/goal berubah → peta telefon lama (koordinat) auto-buang,
  // robot belajar semula (elak guna koordinat basi). v2: pick guna bulatan tick.
  // v3 (31 Okt): buang peta rosak (ai_label 0,0 · tag_product di kawasan OK).
  version: 3,
  // Tetingkap fokus tiap skrin — diukur 31 Okt (Redmi 14C). Skrin post TAK boleh dibaca
  // ("could not get idle state", ~15 saat), jadi gate guna fokus + piksel, bukan dump.
  screens: { post: 'PublishVideoActivity', addProduct: 'ReactActivity_', productSheet: 'ReactTransparentActivity_', profile: 'ReactActivity_', player: 'NativePlayerActivity', home: 'HomeActivity_', camera: 'SSZMediaActivity', editor: 'SSZMultipleEditActivity' },
  // Kedudukan terukur (ratio skrin 720x1640) — lapis default sebelum vision. ai_label =
  // kedudukan SELEPAS produk ditag (layout turun).
  measuredPositions: {
    open_video: { rx: 430 / 720, ry: 1490 / 1640 }, // tab Live & Video, nav bawah
    tap_plus: { rx: 680 / 720, ry: 124 / 1640 }, // butang + di feed
    popup_x: { rx: 368 / 720, ry: 1253 / 1640 }, // X bulatan putih popup Shopee Drama (gambar 29 Okt)
    finish_post: { rx: 410 / 720, ry: 1480 / 1640 }, // butang Post, ukur 1 Nov
    profile_icon: { rx: 49 / 720, ry: 124 / 1640 }, // ikon profil di feed Live & Video
    caption: { rx: 400 / 720, ry: 220 / 1640 },
    tag_product: { rx: 520 / 720, ry: 538 / 1640 },
    import_url: { rx: 667 / 720, ry: 112 / 1640 }, // ikon link page Add Product
    ai_label: { rx: 640 / 720, ry: 1239 / 1640 },
    finish_draft: { rx: 71 / 720, ry: 1480 / 1640 },
  },
  // Pintu nav bawah Shopee (selector stabil, dari Dump sebenar 29 Okt).
  steps: [
    {
      id: 'launch', kind: 'launch', labelEn: 'Open Shopee', labelMs: 'Buka Shopee',
      selector: null, goal: '',      calibratable: false,
    },
    {
      id: 'open_video', kind: 'open_feed', labelEn: 'Open Video tab', labelMs: 'Masuk Video',
      // Nav bawah: tab_bar_button_{home,mall,video_and_live,notification,me}
      selector: { id: 'com.shopee.my:id/tab_bar_button_video_and_live', text: 'Live & Video' },
      goal: 'Tap the "Live & Video" tab in the bottom navigation bar',
      calibratable: true,
    },
    {
      id: 'tap_plus', kind: 'open_create', labelEn: 'Tap create (+)', labelMs: 'Tekan cipta (+)',
      selector: null, // skrin beranimasi → tiada selector
      goal: 'Tap the "+" create button at the top-right of the Live & Video screen',
      calibratable: true,
      verify: { focusIncludes: 'SSZMediaActivity' }, // skrin kamera
    },
    {
      id: 'open_library', kind: 'tap', labelEn: 'Open Library', labelMs: 'Buka Library',
      selector: { id: 'com.shopee.my:id/tv_gallery_entrance' },
      goal: 'On the camera screen, tap the "Library" button at the bottom-right to pick an existing video',
      calibratable: true,
      verify: { anchorPresent: { id: 'com.shopee.my:id/tv_pick_next' } }, // galeri terbuka
    },
    {
      id: 'pick_video', kind: 'pick_video', labelEn: 'Select SESAAT video', labelMs: 'Pilih video SESAAT',
      selector: null, // runner tekan tab Video + bulatan tick tile pertama
      goal: '', calibratable: false,
    },
    {
      id: 'gallery_next', kind: 'tap', labelEn: 'Confirm selection', labelMs: 'Sahkan pilihan',
      selector: { id: 'com.shopee.my:id/tv_pick_next' },
      // Lepas tick → tekan "Next (1)" bawah-kanan galeri → terus ke editor
      // (elak skrin preview). Next (1) = 1 video dipilih.
      goal: 'In the gallery, tap the "Next" button (it shows "Next (1)") at the bottom-right to continue',
      calibratable: true,
      // PENGESAH: lepas Next (1) kita WAJIB sampai skrin editor (tv_compress).
      // Tak sampai → skrin tak maju → merah (bukan hijau palsu).
      verify: { focusIncludes: 'SSZMultipleEditActivity', ms: 20000 },
    },
    {
      id: 'editor_next', kind: 'tap', labelEn: 'Confirm edit', labelMs: 'Sahkan edit',
      // ⚠️ id SAHAJA (buang text 'Next') — skrin lain pun ada "Next"; padan ikut
      //    resource-id elak tertekan Next skrin salah (punca skip 29 Okt).
      selector: { id: 'com.shopee.my:id/tv_compress' },
      goal: 'On the editor screen (Trimmer/Text/Stickers/Voiceover tabs), tap "Next" at the bottom-right',
      calibratable: true,
      // PENGESAH: lepas editor Next kita WAJIB tinggalkan editor (tv_compress hilang).
      // Masih ada → belum maju → cuba lagi → merah. Elak caption/produk atas editor.
      verify: { focusIncludes: 'PublishVideoActivity', ms: 30000 }, // video panjang lambat diproses
    },
    {
      id: 'caption', kind: 'input_caption', labelEn: 'Write caption', labelMs: 'Isi caption',
      selector: null, // skrin Post beranimasi → vision/coord
      goal: 'On the post details screen, tap the caption text field at the top (near "Add Caption")',
      calibratable: true,
    },
    {
      id: 'tag_product', kind: 'tag_product', labelEn: 'Tag product', labelMs: 'Tag produk',
      selector: null,
      goal: 'On the post details screen, tap "Add product" / "Tap to add products"',
      calibratable: true,
    },
    {
      id: 'ai_label', kind: 'toggle_ai', labelEn: 'Turn on AI label', labelMs: 'Hidupkan AI label',
      selector: null,
      goal: 'On the post details screen, turn ON the "AI-generated label" toggle on the right',
      calibratable: true,
    },
    {
      id: 'finish', kind: 'finish', labelEn: 'Publish / Save draft', labelMs: 'Terbit / Simpan draft',
      selector: null,
      // goal diisi ikut mod dalam runner: "Post" (oren, bawah) atau "Drafts" (bawah-kiri)
      goal: '',      calibratable: true,
    },
  ],
};

// Sub-langkah tag produk (dipakai runner bila kind === 'tag_product').
// Dari ukuran 29 Okt: Add product → tampal link (ikon link atas-kanan) →
// Search/senarai → butang "Add" per produk → sheet "Edit product info" → "Done".
export const TAG_PRODUCT_SUBSTEPS = [
  { id: 'tag_paste_link', goal: 'Tap the link icon at the top-right of the Add Product screen to paste a product link' },
  { id: 'tag_add', goal: 'Tap the "Add" button next to the product that appears' },
  { id: 'tag_done', goal: 'On the "Edit product info" sheet, tap "Done" (do NOT tap the product row itself)' },
];

// TikTok app phone (4 Nov 2026) — enjin BERBEZA: skrin boleh dibaca → robot ikut teks
// (androidTiktok.js), tiada kalibrasi. runRecipe (androidRunner) menghantar ke `run`.
// `steps` = senarai video (lalai); `stepsFor(content)` ikut jenis post.
const TIKTOK_RECIPE = {
  appId: 'tiktok',
  pkg: TIKTOK_PKG,
  version: 1,
  engine: 'dump',
  steps: tiktokSteps('video'),
  stepsFor: tiktokSteps,
  run: runTiktokPhone,
};

// Facebook app phone (4 Nov 2026) — enjin 'dump' sama TikTok (androidFacebook.js).
// stepsFor(content, { page, link }) — langkah berubah ikut page + link produk.
const FACEBOOK_RECIPE = {
  appId: 'facebook',
  pkg: FB_PKG,
  version: 1,
  engine: 'dump',
  steps: facebookSteps('video'),
  stepsFor: facebookSteps,
  run: runFacebookPhone,
};

// Daftar resipi ikut app. Tambah app baru = tambah satu entri.
export const RECIPES = { shopee: SHOPEE_RECIPE, tiktok: TIKTOK_RECIPE, facebook: FACEBOOK_RECIPE };

export function recipeFor(appId) {
  return RECIPES[appId] || null;
}

// Teks matlamat langkah akhir ikut mod. post = tekan Post (terbit public,
// tak boleh undo). draft = tekan Drafts (selamat, pelanggan terbit sendiri).
export function finishGoal(mode) {
  return mode === 'post'
    ? 'Tap the orange "Post" button at the bottom to publish'
    : 'Tap the "Drafts" button at the bottom-left to save as draft';
}
